import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Login Redux Slice
 *
 * NOTE: This slice manages UI-related auth state only:
 * - OTP flow state (requiresOtp, userId, masked contact info)
 * - Organization branding
 * - Loading states
 * - Login/logout flags
 *
 * IMPORTANT: The AuthContext is the source of truth for user authentication.
 * The 'user' field here is kept for backward compatibility but should not be
 * used directly - prefer useAuth().user instead.
 */

interface User {
    id?: string;
    username?: string;
    email?: string;
    name?: string;
    mobile?: string;
    accessLevel?: string;
    organizationId?: number;
    [key: string]: any;
}

interface OrganizationBranding {
    organizationId?: number;
    org_name?: string;
    logo?: string;
    banner_image?: string;
    favicon?: string;
    login_screen_images?: string[];
    branding_colors?: Record<string, any>;
    welcome_message?: string;
    requires_2fa?: boolean;
    user_type?: string;
    purchased_systems?: string[];
}

interface LoginState {
    user: User | null;
    error: string | null;
    success: boolean;
    isUserLogout: boolean;
    loading: boolean;
    menuLoading: boolean;
    requiresOtp: boolean;
    userId: string | null;
    maskedMobile: string | null;
    maskedEmail: string | null;
    loginType: string | null;
    organizationBranding: OrganizationBranding | null;
    lookupData: any | null;
}

const initialState: LoginState = {
    user: null,
    error: null,
    success: false,
    isUserLogout: false,
    loading: false,
    menuLoading: false,
    requiresOtp: false,
    userId: null,
    maskedMobile: null,
    maskedEmail: null,
    loginType: null,
    organizationBranding: null,
    lookupData: null,
};

const loginSlice = createSlice({
    name: "login",
    initialState,
    reducers: {
        loginRequest(state: LoginState) {
            state.loading = true;
            state.error = null;
            state.success = false;
        },
        loginSuccess(state: LoginState, action: PayloadAction<any>) {
            state.user = action.payload.user || action.payload;
            state.success = true;
            state.loading = false;
            state.error = null;
            state.requiresOtp = false;
        },
        loginError(state: LoginState, action: PayloadAction<string | any>) {
            state.error = action.payload;
            state.success = false;
            state.loading = false;
        },
        loginRequiresOtp(state: LoginState, action: PayloadAction<any>) {
            state.requiresOtp = true;
            state.userId = action.payload.userId;
            state.maskedMobile = action.payload.maskedMobile || action.payload.mobile;
            state.maskedEmail = action.payload.maskedEmail || action.payload.email;
            state.loading = false;
            state.error = null;
        },
        clearOtpState(state: LoginState) {
            state.requiresOtp = false;
            state.userId = null;
            state.maskedMobile = null;
            state.maskedEmail = null;
        },
        logoutSuccess(state: LoginState, action: PayloadAction<boolean>) {
            // Reset to initial state completely
            state.isUserLogout = action.payload;
            state.user = null;
            state.success = false;
            state.requiresOtp = false;
            state.userId = null;
            state.maskedMobile = null;
            state.maskedEmail = null;
            state.error = null;
            state.loading = false;
            state.menuLoading = false;
            state.loginType = null;
            state.organizationBranding = null;
            state.lookupData = null;
        },
        setOrganizationBranding(state: LoginState, action: PayloadAction<OrganizationBranding>) {
            state.organizationBranding = action.payload;
        },
        setLookupData(state: LoginState, action: PayloadAction<any>) {
            state.lookupData = action.payload;
        },
        clearLoginError(state: LoginState) {
            state.error = null;
        },
        setLoginType(state: LoginState, action: PayloadAction<string>) {
            state.loginType = action.payload;
        },
        resetLogoutFlag(state: LoginState) {
            state.isUserLogout = false;
        },
        menuLoadingStart(state: LoginState) {
            state.menuLoading = true;
        },
        menuLoadingEnd(state: LoginState) {
            state.menuLoading = false;
        }
    },
});

export const {
    loginRequest,
    loginSuccess,
    loginError,
    loginRequiresOtp,
    clearOtpState,
    logoutSuccess,
    setOrganizationBranding,
    setLookupData,
    clearLoginError,
    setLoginType,
    resetLogoutFlag,
    menuLoadingStart,
    menuLoadingEnd
} = loginSlice.actions;

export default loginSlice.reducer;
